import {
  AfterViewInit,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  NgZone,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';

// Helpers
import {
  emailValidator,
  getQueryParamByName,
  localStorageSetter,
} from '../../../helpers';

// Store and observable operators
import { Store } from '@ngrx/store';
import { GET_PROFILE } from '../../../../../ngrx/actions';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Subscription, throwError } from 'rxjs';

// miscellaneous
import isEmpty from 'lodash-es/isEmpty';
import { environment } from '../../../../../environments/environment';

// services
import { AuthenticationService } from '../../../authentication.service';
import { HeaderService } from '../../../../header/header.service';
import { NavigationService } from '../../../navigation.service';

// Interface
import { SignInInterface } from '../../../models/interfaces';
import { getAppHref } from 'navigation/nav-shared/utils/link.utils';
import { DOCUMENT } from '@angular/common';

export interface PrivacyPolicyRequiredPayload {
  consumerId: string;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

/** DevTools filter: `usr-sign-in-form privacy` */
const PRIVACY_LOG = '[usr-sign-in-form privacy]';

@Component({
  selector: 'usr-sign-in-form',
  templateUrl: './sign-in-form.component.html',
  styleUrls: ['./sign-in-form.component.scss'],
})
export class SignInFormComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() checkbox: boolean = true;
  @Input() emailAddress = '';
  @Input() signInFocus: string = '';
  @Input() signInMode: string;
  @Input() titleStyle: string;

  @Output() pageRedirect = new EventEmitter();
  @ViewChild('inputEmail', { static: false }) emailInput;
  @ViewChild('pwInput', { static: false }) passwordInput;

  createAccountHref = getAppHref('/secure/register');
  forgotPwLink = getAppHref('/secure/password-reset-request');
  instanceId = Math.floor(Math.random() * (100 - 1));
  invalidEmail: boolean = false;
  invalidPassword: boolean = false;
  accountMenuActive: boolean = false;
  passwordReset: boolean = false;
  privacyLink: string = `${environment.domain}/about-us/legal/privacy-policy`;
  signInForm: FormGroup;
  subscriptions: Subscription[] = [];
  themeMode: string = null;
  userFocus = false;
  enableSigninButton = false;

  // Privacy policy modal state (owned by this form so all containers reuse it)
  showPrivacyModal: boolean = false;
  pendingUserId: string | null = null;
  pendingSignInData: PrivacyPolicyRequiredPayload | null = null;
  privacyDecisionHandled: boolean = false;

  /** Bound handlers for document-level custom events from the privacy modal WC. */
  private boundPrivacyDecline = () => this.onPrivacyDecline();
  private boundPrivacyAccept = () => this.onPrivacyAccept();
  private boundPrivacyOpenChange = (e: Event) => this.onPrivacyOpenChange(e);

  constructor(
    @Inject(DOCUMENT) private _document: Document,
    private authenticationService: AuthenticationService,
    private fb: FormBuilder,
    private navService: NavigationService,
    private headerService: HeaderService,
    private store: Store<any>,
    private ngZone: NgZone
  ) {}

  ngOnInit() {
    this.createSignInForm();

    // TODO: Use the ActivatedRoute dependency injection, in order to get the querystring values.
    if (this.signInMode === 'sign-in') {
      const resetRedirect = getQueryParamByName('prevPage');
      if (resetRedirect === 'pwReset') {
        this.passwordReset = true;
      }
    }

    this.subscriptions = [
      this.averyThemeModeSubscription(),
      this.myAccountActiveSubscription(),
      this.checkEnableSignInButton(),
    ];

    // Listen for events from the privacy policy web component
    this._document.addEventListener(
      'privacy-decline',
      this.boundPrivacyDecline
    );
    this._document.addEventListener('privacy-accept', this.boundPrivacyAccept);
    this._document.addEventListener(
      'privacy-open-change',
      this.boundPrivacyOpenChange
    );
  }

  ngAfterViewInit() {
    if (this.signInFocus === 'email') {
      this.emailInput.nativeElement.focus();
    } else if (this.signInFocus === 'password') {
      this.passwordInput.nativeElement.focus();
    }
  }

  ngOnDestroy() {
    this._document.removeEventListener(
      'privacy-decline',
      this.boundPrivacyDecline
    );
    this._document.removeEventListener(
      'privacy-accept',
      this.boundPrivacyAccept
    );
    this._document.removeEventListener(
      'privacy-open-change',
      this.boundPrivacyOpenChange
    );
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  /**
   * Completes the sign-in flow after the user accepts the privacy policy in the modal.
   * Writes tokens, fetches user info and stencils, then redirects.
   */
  completeSignInAfterPrivacyAccept(
    payload: PrivacyPolicyRequiredPayload
  ): void {
    const { consumerId, accessToken, refreshToken, expiresIn } = payload;
    console.info(PRIVACY_LOG, 'completeSignInAfterPrivacyAccept start', {
      signInMode: this.signInMode,
      consumerId,
      hasAccessToken: !!accessToken,
      hasRefreshToken: !!refreshToken,
      expiresIn,
      href: typeof location !== 'undefined' ? location.href : '',
    });
    const accessData = JSON.stringify({
      u_at: accessToken,
      expiry: Date.now() + expiresIn * 1000,
    });
    localStorageSetter('u_at', accessData);
    localStorageSetter('u_rt', refreshToken);

    this.authenticationService
      .getUserInfo(accessToken)
      .pipe(
        switchMap((data: any) => {
          this.store.dispatch({
            type: GET_PROFILE,
            payload: {
              email: data.email,
              firstName: data.firstName,
              lastName: data.lastName,
              optIn: data.optIn,
            },
          });
          localStorageSetter('u_i', JSON.stringify(data));
          this.authenticationService.userSignedInUpdate(true);
          if (!isEmpty(consumerId)) {
            this.authenticationService.setGAUser(consumerId);
            this.authenticationService.DYEventLogin(consumerId);
          }
          return this.authenticationService.getStencilList();
        })
      )
      .subscribe(
        (data) => {
          if (!isEmpty(data)) {
            const aryStencils = data.filter((stencil: any) => {
              if (stencil.stencilOrders.length > 0) return stencil;
            });
            this.authenticationService.updateStencilStorage(aryStencils);
            this.authenticationService.stencilExists.next(
              aryStencils.length > 0
            );
          } else {
            this.authenticationService.stencilExists.next(false);
          }
          const _window = this.navService.getNativeWindow();
          switch (this.signInMode) {
            case 'sign-in':
              {
                const urlRedirect = getQueryParamByName('returnUrl');
                const pageRedirect = getQueryParamByName('returnPage');
                const inAngular = pageRedirect
                  ? this.navService.routeIsInAngularApp(pageRedirect)
                  : false;
                console.info(PRIVACY_LOG, 'post-privacy redirect (success)', {
                  returnUrl: urlRedirect || null,
                  returnPage: pageRedirect || null,
                  routeIsInAngularApp: inAngular,
                  action: !isEmpty(urlRedirect)
                    ? 'location:returnUrl'
                    : !isEmpty(pageRedirect) && inAngular
                    ? 'location:origin+returnPage'
                    : 'location:origin+/myaccount/projects',
                });
                if (!isEmpty(urlRedirect)) {
                  _window.location.href = urlRedirect;
                } else if (
                  !isEmpty(pageRedirect) &&
                  this.navService.routeIsInAngularApp(pageRedirect)
                ) {
                  const href = `${_window.location.origin}${pageRedirect}`;
                  _window.location.href = href;
                } else {
                  const href = `${_window.location.origin}/myaccount/projects`;
                  _window.location.href = href;
                }
              }
              break;
            case 'dropdown':
              _window.location.reload();
              break;
            case 'sign-in-modal':
              console.info(PRIVACY_LOG, 'post-privacy: sign-in-modal branch', {
                magento: this.navService.routeIsMagentoApp(),
              });
              this.authenticationService.downloadStatus.next('download');
            default:
              if (this.navService.routeIsMagentoApp()) {
                this.navService.getMagento().subscribe();
              }
          }
        },
        (err) => {
          console.error(
            PRIVACY_LOG,
            'completeSignInAfterPrivacyAccept failed (getUserInfo or stencils)',
            err
          );
          this.authenticationService.stencilExists.next(false);
          if (this.signInMode === 'sign-in') {
            const urlRedirect = getQueryParamByName('returnUrl');
            const pageRedirect = getQueryParamByName('returnPage');
            const _window = this.navService.getNativeWindow();
            console.warn(
              PRIVACY_LOG,
              'post-privacy fallback redirect (error path)',
              {
                returnUrl: urlRedirect || null,
                returnPage: pageRedirect || null,
              }
            );
            if (!isEmpty(urlRedirect)) {
              _window.location.href = urlRedirect;
            } else if (
              !isEmpty(pageRedirect) &&
              this.navService.routeIsInAngularApp(pageRedirect)
            ) {
              _window.location.href = `${_window.location.origin}/${pageRedirect}`;
            } else {
              _window.location.href = `${_window.location.origin}/myaccount/projects`;
            }
          } else if (this.navService.routeIsMagentoApp()) {
            this.navService.getMagento().subscribe();
          }
        }
      );
  }

  /**
   * Return subscription for the theme mode
   *
   * @returns {Subscription}
   * @memberof SignInFormComponent
   */
  averyThemeModeSubscription(): Subscription {
    return this.navService.themeMode.subscribe(
      (type: string) => (this.themeMode = type)
    );
  }

  /**
   * Return subscription for My Account Menu Activation.
   *
   * @returns {Subscription}
   * @memberof SignInFormComponent
   */
  myAccountActiveSubscription(): Subscription {
    return this.headerService.myAccountActive.subscribe((value) => {
      this.accountMenuActive = value;
      if (value) {
        this.signInForm.reset();
      }
    });
  }

  /**
   * Check if sign-in button should be enabled based on form validity
   *
   * @returns {Subscription} Subscription to form status changes
   */
  checkEnableSignInButton(): Subscription {
    return this.signInForm.statusChanges.subscribe(() => {
      this.enableSigninButton =
        this.signInForm && this.signInEmail?.valid && this.signInPass?.valid;
    });
  }

  /**
   * Handles creating to form-group controllers for the sign-in form.
   *
   * @memberOf SignInFormComponent
   */
  createSignInForm() {
    // this condition will determine whether or not checkbox in the form is required for validation.
    if (this.checkbox) {
      this.signInForm = this.fb.group({
        email: [
          this.emailAddress,
          Validators.compose([Validators.required, emailValidator]),
        ],
        password: ['', Validators.compose([Validators.required])],
        acknowledgeTOS: [false, Validators.required],
      });
    } else {
      this.signInForm = this.fb.group({
        email: [
          this.emailAddress,
          Validators.compose([Validators.required, emailValidator]),
        ],
        password: ['', Validators.compose([Validators.required])],
      });
    }
  }

  /**
   * Redirects the user to the registration page
   * @memberOf MyAccountMenuComponent
   */
  openCreateAccount(event) {
    event.preventDefault();
    window.location.href = this.createAccountHref;
    this.headerService.shadowOverlay.next(false);
    this.headerService.navMenuActive.next(false);
  }

  /**
   * Angular Forms boilerplate to include interactive sign-in form
   *
   * @return {AbstractControl | null}
   */
  get signInEmail() {
    return this.signInForm.get('email');
  }
  get signInPass() {
    return this.signInForm.get('password');
  }

  /**
   * Handles boolean variable that is used to detect if an input is focused or not.
   *
   * @memberOf SignInFormComponent
   */
  changeFocus() {
    if (!this.userFocus) {
      this.userFocus = true;
    } else {
      this.userFocus = false;
    }

    if (this.invalidPassword) {
      this.invalidPassword = !this.invalidPassword;
    }
    if (this.invalidEmail) {
      this.invalidEmail = !this.invalidEmail;
    }
  }

  /**
   * Handles the user's submission to sign into my accounts, and handle the authentication verification of a user
   * after they've successfully signed in.
   *
   * @memberOf SignInFormComponent
   */
  signInUser() {
    let signInErr = false;
    let consumerId = null;
    let needsPolicyUpdate = false;

    if (this.signInForm.valid) {
      this.authenticationService
        .signInUser(
          this.signInEmail.value.trim().toLowerCase(),
          this.signInPass.value
        )
        .pipe(
          catchError((error: HttpErrorResponse) => {
            signInErr = true;
            return throwError(error);
          }),
          switchMap((authData: SignInInterface) => {
            consumerId = authData.uid;
            return this.authenticationService.checkPrivacy(consumerId).pipe(
              catchError(
                (err: {
                  privacyCheckFailed?: boolean;
                  consumerId?: string;
                }) => {
                  if (err && err.privacyCheckFailed) {
                    return throwError({
                      ...err,
                      accessToken: authData.access_token,
                      refreshToken: authData.refresh_token,
                      expiresIn: authData.expires_in,
                    });
                  }
                  return throwError(err);
                }
              ),
              switchMap(() => {
                const accessData = JSON.stringify({
                  u_at: authData.access_token,
                  expiry: Date.now() + authData.expires_in * 1000,
                });
                localStorageSetter('u_at', accessData);
                localStorageSetter('u_rt', authData.refresh_token);
                return this.authenticationService.getUserInfo(
                  authData.access_token
                );
              })
            );
          }),
          switchMap((data: any) => {
            this.store.dispatch({
              type: GET_PROFILE,
              payload: {
                email: data.email,
                firstName: data.firstName,
                lastName: data.lastName,
                optIn: data.optIn,
              },
            });
            localStorageSetter('u_i', JSON.stringify(data));
            this.authenticationService.userSignedInUpdate(true);
            if (!isEmpty(consumerId)) {
              this.authenticationService.setGAUser(consumerId);
              this.authenticationService.DYEventLogin(consumerId);
            }

            return this.authenticationService.getStencilList();
          })
        )
        .subscribe(
          (data) => {
            if (!isEmpty(data)) {
              const aryStencils = data.filter((stencil: any) => {
                if (stencil.stencilOrders.length > 0) {
                  return stencil;
                }
              });
              this.authenticationService.updateStencilStorage(aryStencils);
              this.authenticationService.stencilExists.next(
                aryStencils.length > 0
              );
            } else {
              this.authenticationService.stencilExists.next(false);
            }

            // Checks to see if the user is signing from the sign in page, and handle redirecting users to the appropriate
            // my account page.
            switch (this.signInMode) {
              case 'sign-in':
                const urlRedirect = getQueryParamByName('returnUrl');
                const pageRedirect = getQueryParamByName('returnPage');
                const _window = this.navService.getNativeWindow();

                if (!isEmpty(urlRedirect)) {
                  _window.location.href = urlRedirect;
                } else if (
                  !isEmpty(pageRedirect) &&
                  this.navService.routeIsInAngularApp(pageRedirect)
                ) {
                  // _window.location.href = `${_window.location.origin}${pageRedirect}`;
                  this.pageRedirect.emit(pageRedirect);
                } else {
                  this.pageRedirect.emit('/myaccount/projects');
                  // _window.location.href = `${_window.location.origin}/myaccount/projects`;
                }
                break;
              case 'dropdown':
                this.navService.getNativeWindow().location.reload();
                break;
              case 'sign-in-modal':
                this.authenticationService.downloadStatus.next('download');
              default:
                if (this.navService.routeIsMagentoApp()) {
                  this.navService.getMagento().subscribe();
                }
            }
          },
          (
            error:
              | HttpErrorResponse
              | {
                  privacyCheckFailed?: boolean;
                  consumerId?: string;
                  accessToken?: string;
                  refreshToken?: string;
                  expiresIn?: number;
                }
          ) => {
            if (
              error &&
              (error as any).privacyCheckFailed &&
              (error as any).consumerId
            ) {
              this.authenticationService.userSignedIn.next(false);
              this.privacyDecisionHandled = false;
              this.pendingSignInData = {
                consumerId: (error as any).consumerId!,
                accessToken: (error as any).accessToken!,
                refreshToken: (error as any).refreshToken!,
                expiresIn: (error as any).expiresIn!,
              };
              this.pendingUserId = (error as any).consumerId!;
              this.showPrivacyModal = true;
              // Clear server session (e.g. cookie) so that if the user navigates away
              // without accepting, validate-user will not see a valid session and
              // the user cannot bypass the privacy modal on the next page.
              const token = (error as any).accessToken;
              console.info(
                PRIVACY_LOG,
                'privacy policy required — showing modal',
                {
                  consumerId: (error as any).consumerId,
                  callingSignOutToClearSession: !!token,
                }
              );
              if (token) {
                this.authenticationService.signOut(token).subscribe({
                  error: (e) =>
                    console.warn(
                      PRIVACY_LOG,
                      'signOut after privacy gate failed',
                      e
                    ),
                });
              }
              return;
            }
            if (signInErr) {
              const errResponse = (error as HttpErrorResponse).error;
              this.authenticationService.userSignedIn.next(false);
              this.invalidPassword = true;
              if (errResponse.errorMessage === 'User does not exist.') {
                this.invalidEmail = true;
              }
            } else {
              // Handles errors from the stencil services, and continues to redirect the user.
              this.authenticationService.stencilExists.next(false);

              if (this.signInMode === 'sign-in') {
                const urlRedirect = getQueryParamByName('returnUrl');
                const pageRedirect = getQueryParamByName('returnPage');
                const _window = this.navService.getNativeWindow();
                if (!isEmpty(urlRedirect)) {
                  _window.location.href = `${urlRedirect}`;
                } else if (
                  !isEmpty(pageRedirect) &&
                  this.navService.routeIsInAngularApp(pageRedirect)
                ) {
                  _window.location.href = `${_window.location.origin}/${pageRedirect}`;
                } else {
                  _window.location.href = `${_window.location.origin}/myaccount/projects`;
                }
              } else {
                if (this.navService.routeIsMagentoApp()) {
                  this.navService.getMagento().subscribe();
                }
              }
            }
          }
        );
    } else {
      this.signInEmail.markAsDirty();
      this.signInPass.markAsDirty();
    }
  }

  /**
   * Handles Decline coming from the privacy policy web component.
   * Logs the user out on the server (using the in-memory access token) and resets state.
   */
  private onPrivacyDecline() {
    this.ngZone.run(() => {
      console.info(PRIVACY_LOG, 'document event: privacy-decline', {
        hadPendingPayload: !!this.pendingSignInData,
      });
      if (!this.pendingSignInData) {
        this.showPrivacyModal = false;
        return;
      }
      this.privacyDecisionHandled = true;
      this.authenticationService.userSignedIn.next(false);
      this.showPrivacyModal = false;
      this.pendingSignInData = null;
      this.pendingUserId = null;
    });
  }

  /**
   * Handles Accept coming from the privacy policy web component.
   * Calls SSO sign-in again to obtain fresh tokens: we already invoked signOut()
   * when showing this modal, so the previous access token is no longer valid.
   */
  private onPrivacyAccept() {
    this.ngZone.run(() => {
      console.info(PRIVACY_LOG, 'document event: privacy-accept', {
        hadPendingPayload: !!this.pendingSignInData,
        consumerId: this.pendingSignInData?.consumerId ?? null,
      });
      if (!this.pendingSignInData) {
        console.warn(
          PRIVACY_LOG,
          'privacy-accept ignored — no pendingSignInData (events out of order or duplicate handler?)'
        );
        this.showPrivacyModal = false;
        return;
      }
      this.privacyDecisionHandled = true;

      const email = this.signInEmail?.value?.trim()?.toLowerCase();
      const password = this.signInPass?.value;

      this.showPrivacyModal = false;
      this.pendingSignInData = null;
      this.pendingUserId = null;

      if (!email || !password) {
        console.error(
          PRIVACY_LOG,
          'privacy-accept: email/password missing — cannot re-sign-in after logout'
        );
        this.authenticationService.userSignedIn.next(false);
        return;
      }

      this.authenticationService
        .signInUser(email, password)
        .pipe(
          switchMap((authData: SignInInterface) =>
            this.authenticationService
              .checkPrivacy(authData.uid)
              .pipe(map(() => authData))
          )
        )
        .subscribe({
          next: (authData: SignInInterface) => {
            this.completeSignInAfterPrivacyAccept({
              consumerId: authData.uid,
              accessToken: authData.access_token,
              refreshToken: authData.refresh_token,
              expiresIn: authData.expires_in,
            });
          },
          error: (
            error:
              | HttpErrorResponse
              | { privacyCheckFailed?: boolean; consumerId?: string }
          ) => {
            console.error(
              PRIVACY_LOG,
              'privacy-accept re-sign-in or privacy check failed',
              error
            );
            this.authenticationService.userSignedIn.next(false);
            if (error instanceof HttpErrorResponse) {
              const errResponse = error.error;
              this.invalidPassword = true;
              if (errResponse?.errorMessage === 'User does not exist.') {
                this.invalidEmail = true;
              }
            }
          },
        });
    });
  }

  /**
   * Handles close from the modal itself (e.g. ESC or backdrop click).
   * In practice, the current web component fires `privacy-open-change` when the user clicks Accept.
   */
  private onPrivacyOpenChange(event: Event) {
    const detail = (event as CustomEvent<{ open: boolean }>).detail;
    console.info(PRIVACY_LOG, 'document event: privacy-open-change', {
      detail,
      privacyDecisionHandled: this.privacyDecisionHandled,
      hadPendingPayload: !!this.pendingSignInData,
    });
    if (detail?.open === false) {
      if (!this.privacyDecisionHandled && this.pendingSignInData) {
        console.info(
          PRIVACY_LOG,
          'privacy-open-change (closed) → invoking onPrivacyAccept fallback'
        );
        this.onPrivacyAccept();
      } else {
        this.showPrivacyModal = false;
        this.pendingSignInData = null;
        this.pendingUserId = null;
      }
    }
  }
}
